import { gql } from "@apollo/client";

// Lives under `WebAPI/` rather than `ELD/` so Drive's codegen `documents` glob —
// which only excludes `WebAPI/**` — doesn't try to validate these operations
// against the Drive schema. Types below are hand-written rather than generated,
// for the same reason `WebAPI/Revision` is: a `documents` glob validates every
// operation against *deployed* staging, and `resubmitEldOutputFile` plus the
// window fields on `SendELDToFMCSAResult` arrive with SC-559054. Swap this
// module over to `npm run generate` once that is deployed.

/**
 * Outcome of a submission, flattened by the API to the one value both stacks can
 * express. Failures are reported at the stage that failed, in the order the
 * stages run — so `GENERATION_FAILED` never reached FMCSA at all, while
 * `TRANSMISSION_FAILED` did and was rejected.
 */
export type Status =
  | "PENDING"
  | "TRANSMISSION_SUCCEEDED"
  | "GENERATION_FAILED"
  | "VALIDATION_FAILED"
  | "TRANSMISSION_FAILED";

export type TransmissionMethod = "EMAIL" | "SOAP";

export type SubmissionAsset = {
  readonly assetId: number;
  readonly name: string | null;
};

export type SubmissionFragment = {
  readonly id: string;
  readonly driverId: number;
  readonly driverName: string | null;
  readonly createdAt: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: Status;
  /**
   * Detail behind the status: the failure reason, or a non-fatal remark on a
   * success. A succeeded submission carrying one is what the legacy stack called
   * a `WARNING` — received by FMCSA, with data issues.
   */
  readonly statusMessage: string | null;
  readonly outputFileComment: string | null;
  /** Empty for elogs submissions until SC-559295 records them. */
  readonly assets: readonly SubmissionAsset[];
  /** Null for elogs submissions until SC-559295 records the submitter. */
  readonly submittedByName: string | null;
  /** Null for elogs submissions until SC-559295 records the recipient. */
  readonly sentToEmail: string | null;
};

// `source` and `transmissionMethod` are deliberately not selected: the table
// shows neither, and the point of the merged list is that a row reads the same
// whichever stack produced it.
const SubmissionFields = `
  id
  driverId
  driverName
  createdAt
  startDate
  endDate
  status
  statusMessage
  outputFileComment
  assets {
    assetId
    name
  }
  submittedByName
  sentToEmail
`;

export const CompanyEldSubmissionsDocument = gql`
  query CompanyEldSubmissions(
    $startDate: String!
    $endDate: String
    $first: Int
    $after: String
  ) {
    companyEldSubmissions(
      startDate: $startDate
      endDate: $endDate
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          ${SubmissionFields}
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export type CompanyEldSubmissionsVariables = {
  /** Inclusive start of the window (YYYY-MM-DD), applied to when each submission was made. */
  readonly startDate: string;
  /** Inclusive end of the window (YYYY-MM-DD). The API defaults to today. */
  readonly endDate?: string;
  readonly first?: number;
  readonly after?: string;
};

export type CompanyEldSubmissionsResult = {
  readonly companyEldSubmissions: {
    readonly edges:
      | readonly {
          readonly cursor: string;
          readonly node: SubmissionFragment | null;
        }[]
      | null;
    readonly pageInfo: {
      readonly hasNextPage: boolean;
      readonly endCursor: string | null;
    };
  } | null;
};

/**
 * The window a file actually covers, which is not always the window that was
 * asked for: a migrated driver's history on the new stack begins shortly before
 * their cutover, and a request reaching further back is truncated rather than
 * refused. Both are null only when resubmitting a legacy submission, which the
 * legacy stack re-queues from its own record without reporting the window back.
 *
 * Every payload holding one is nullable, as the rest of this module's shapes
 * are. These types are hand-written against the SDL rather than generated, so
 * they are an assertion about the API, not a fact derived from it — and the one
 * assertion not worth making is that a field can never be absent.
 */
export type SubmissionResult = {
  readonly submissionId: string;
  readonly startDate: string | null;
  readonly endDate: string | null;
};

const SubmissionResultFields = `
  submissionId
  startDate
  endDate
`;

export const SubmitEldOutputFileForDriverDocument = gql`
  mutation SubmitEldOutputFileForDriver(
    $input: SubmitEldOutputFileForDriverInput!
  ) {
    submitEldOutputFileForDriver(input: $input) {
      ${SubmissionResultFields}
    }
  }
`;

export type SubmitEldOutputFileForDriverInput = {
  readonly driverId: number;
  /** ISO 8601 date-only start of the reporting window, no more than 59 days before `endDate`. */
  readonly startDate: string;
  /** ISO 8601 date-only end of the reporting window. The API defaults to today. */
  readonly endDate?: string;
  /** Optional 0–60 character key phrase from a safety official. */
  readonly comment?: string;
  readonly transmissionMethod: TransmissionMethod;
  // `test` is deliberately omitted. It marks the file itself as a test file, and
  // is decided by environment rather than by the submitter: the API forces it on
  // for migrated drivers outside production, and the legacy stack applies its own
  // rule to the drivers it still owns.
};

export type SubmitEldOutputFileForDriverVariables = {
  readonly input: SubmitEldOutputFileForDriverInput;
};

export type SubmitEldOutputFileForDriverResult = {
  readonly submitEldOutputFileForDriver: SubmissionResult | null;
};

export type EldCertificationFailureType = {
  readonly eldCertificationFailureTypeId: number;
  readonly description: string;
};

export type EldCertificationDateRange = {
  readonly startDate: string;
  readonly endDate: string;
};

/**
 * Every list here is nullable, for the reason given on {@link SubmissionResult}
 * above: these types are hand-written against the SDL rather than generated, so
 * they assert what the API does rather than derive it. The SDL declares them
 * non-null, but nothing here has checked that against a running server — this
 * query is not yet deployed to staging — and an absent list read as a present
 * one throws on `.map`, which surfaces as "we couldn't check" and hides a
 * refusal the submitter could have acted on.
 *
 * `isEldCertified` is deliberately not nullable. There is no safe default for
 * it: guessing `true` lets a submission through the gate, guessing `false`
 * blocks one with no stated reason. Its absence is decoded as a broken
 * contract instead.
 */
export type EldCertification = {
  readonly isEldCertified: boolean;
  readonly eldCertificationFailureTypes:
    | readonly EldCertificationFailureType[]
    | null;
  readonly missingVinAssetIds: readonly number[] | null;
  readonly invalidVinAssetIds: readonly number[] | null;
  readonly missingDotNumberAssetIds: readonly number[] | null;
  readonly invalidDotNumberAssetIds: readonly number[] | null;
  /**
   * The periods to submit instead, when the requested period spans the
   * driver's migration to the new stack — together covering the request
   * exactly once. Empty otherwise.
   */
  readonly suggestedRanges: readonly EldCertificationDateRange[] | null;
};

export const VerifyEldCertificationDocument = gql`
  query VerifyELDCertification(
    $driverId: Int!
    $startDate: String!
    $endDate: String!
  ) {
    verifyELDCertification(
      driverId: $driverId
      startDate: $startDate
      endDate: $endDate
    ) {
      isEldCertified
      eldCertificationFailureTypes {
        eldCertificationFailureTypeId
        description
      }
      missingVinAssetIds
      invalidVinAssetIds
      missingDotNumberAssetIds
      invalidDotNumberAssetIds
      suggestedRanges {
        startDate
        endDate
      }
    }
  }
`;

export type VerifyEldCertificationVariables = {
  readonly driverId: number;
  /** Inclusive, date-only (YYYY-MM-DD). */
  readonly startDate: string;
  /** Inclusive, date-only (YYYY-MM-DD). */
  readonly endDate: string;
};

export type VerifyEldCertificationResult = {
  readonly verifyELDCertification: EldCertification;
};

export const ResubmitEldOutputFileDocument = gql`
  mutation ResubmitEldOutputFile($submissionId: String!) {
    resubmitEldOutputFile(submissionId: $submissionId) {
      ${SubmissionResultFields}
    }
  }
`;

export type ResubmitEldOutputFileVariables = {
  /**
   * Id of the submission to send again, from `companyEldSubmissions`. Ids from
   * both stacks are accepted and look alike; the API resolves which owns it.
   */
  readonly submissionId: string;
};

export type ResubmitEldOutputFileResult = {
  readonly resubmitEldOutputFile: SubmissionResult | null;
};
